import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import './Profile.css';

interface Notice {
  id: number;
  title: string;
  content: string;
  isImportant: boolean;
  isPublished: boolean;
  publishedAt: string | null;
  createdAt: string;
}

const Profile: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | 'notices'>('profile');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Notice management state
  const [notices, setNotices] = useState<Notice[]>([]);
  const [noticeForm, setNoticeForm] = useState<Partial<Notice>>({
    title: '',
    content: '',
    isImportant: false,
    isPublished: true
  });
  const [editingNoticeId, setEditingNoticeId] = useState<number | null>(null);
  const [showNoticeForm, setShowNoticeForm] = useState(false);

  const hostname = window.location.hostname;
  const API_BASE_URL = process.env.REACT_APP_API_URL ||
    (hostname === 'localhost' ? 'http://localhost:5111' : `http://${hostname}:5111`);

  // Load notices when notices tab is active
  useEffect(() => {
    if (activeTab === 'notices' && user?.role === 'admin') {
      fetchNotices();
    }
  }, [activeTab, user]);

  const fetchNotices = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notice/admin/all?page=1&pageSize=100`);
      const result = await response.json();
      if (result.success) {
        setNotices(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch notices:', error);
      setMessage({ type: 'error', text: '공지사항 목록을 불러오는데 실패했습니다.' });
    }
  };

  const handleNoticeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    try {
      const url = editingNoticeId
        ? `${API_BASE_URL}/api/notice/${editingNoticeId}`
        : `${API_BASE_URL}/api/notice`;
      const method = editingNoticeId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: noticeForm.title,
          content: noticeForm.content,
          isImportant: noticeForm.isImportant || false,
          isPublished: noticeForm.isPublished !== false
        })
      });

      const result = await response.json();
      if (result.success) {
        setMessage({ type: 'success', text: editingNoticeId ? '공지사항이 수정되었습니다.' : '공지사항이 생성되었습니다.' });
        setShowNoticeForm(false);
        setEditingNoticeId(null);
        setNoticeForm({ title: '', content: '', isImportant: false, isPublished: true });
        fetchNotices();
      } else {
        setMessage({ type: 'error', text: result.message || '공지사항 저장에 실패했습니다.' });
      }
    } catch (error) {
      console.error('Failed to save notice:', error);
      setMessage({ type: 'error', text: '공지사항 저장에 실패했습니다.' });
    }
  };

  const handleEditNotice = (notice: Notice) => {
    setNoticeForm({
      title: notice.title,
      content: notice.content,
      isImportant: notice.isImportant,
      isPublished: notice.isPublished
    });
    setEditingNoticeId(notice.id);
    setShowNoticeForm(true);
    setMessage(null);
  };

  const handleDeleteNotice = async (id: number) => {
    if (!window.confirm('정말로 이 공지사항을 삭제하시겠습니까?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/notice/${id}`, {
        method: 'DELETE'
      });
      const result = await response.json();
      if (result.success) {
        setMessage({ type: 'success', text: '공지사항이 삭제되었습니다.' });
        fetchNotices();
      } else {
        setMessage({ type: 'error', text: result.message || '공지사항 삭제에 실패했습니다.' });
      }
    } catch (error) {
      console.error('Failed to delete notice:', error);
      setMessage({ type: 'error', text: '공지사항 삭제에 실패했습니다.' });
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: '새 비밀번호가 일치하지 않습니다.' });
      return;
    }

    try {
      const hostname = window.location.hostname;
      const API_BASE_URL = process.env.REACT_APP_API_URL ||
        (hostname === 'localhost' ? 'http://localhost:5111' : `http://${hostname}:5111`);

      const response = await axios.post(`${API_BASE_URL}/api/auth/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword
      });

      setMessage({ type: 'success', text: response.data.message });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || '비밀번호 변경에 실패했습니다.'
      });
    }
  };

  return (
    <div className="profile-page">
      <div className="container">
        <div className="profile-container">
          <div className="profile-header">
            <h1>내 계정</h1>
            <p>계정 정보를 관리하고 비밀번호를 변경할 수 있습니다.</p>
          </div>

          <div className="profile-tabs">
            <button
              className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('profile');
                setMessage(null);
              }}
            >
              프로필 정보
            </button>
            <button
              className={`tab ${activeTab === 'password' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('password');
                setMessage(null);
              }}
            >
              비밀번호 변경
            </button>
            {user?.role === 'admin' && (
              <button
                className={`tab ${activeTab === 'notices' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('notices');
                  setMessage(null);
                }}
              >
                공지사항 관리
              </button>
            )}
          </div>

          {message && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="profile-content">
              <div className="info-card">
                <h3>계정 정보</h3>
                <div className="info-item">
                  <label>이메일</label>
                  <p>{user?.email}</p>
                </div>
                <div className="info-item">
                  <label>회사명</label>
                  <p>{user?.companyName}</p>
                </div>
                <div className="info-item">
                  <label>계정 유형</label>
                  <p>{user?.role === 'admin' ? '관리자' : '사용자'}</p>
                </div>
                <div className="info-item">
                  <label>크레딧 잔액</label>
                  <p>{user?.creditBalance?.toLocaleString()}원</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'password' && (
            <div className="profile-content">
              <form onSubmit={handlePasswordSubmit} className="profile-form">
                <h3>비밀번호 변경</h3>

                <div className="form-group">
                  <label htmlFor="currentPassword">현재 비밀번호</label>
                  <input
                    id="currentPassword"
                    type="password"
                    className="form-input"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">새 비밀번호</label>
                  <input
                    id="newPassword"
                    type="password"
                    className="form-input"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    minLength={6}
                    required
                  />
                  <small className="form-hint">최소 6자 이상 입력해주세요.</small>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">새 비밀번호 확인</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    className="form-input"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                  />
                </div>

                <button type="submit" className="btn btn-primary">
                  비밀번호 변경
                </button>
              </form>
            </div>
          )}

          {activeTab === 'notices' && user?.role === 'admin' && (
            <div className="profile-content">
              <div className="notices-admin-header">
                <h3>공지사항 관리</h3>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowNoticeForm(true);
                    setEditingNoticeId(null);
                    setNoticeForm({ title: '', content: '', isImportant: false, isPublished: true });
                    setMessage(null);
                  }}
                >
                  + 새 공지사항
                </button>
              </div>

              {showNoticeForm && (
                <form onSubmit={handleNoticeSubmit} className="profile-form notice-form">
                  <h4>{editingNoticeId ? '공지사항 수정' : '새 공지사항 작성'}</h4>

                  <div className="form-group">
                    <label htmlFor="noticeTitle">제목</label>
                    <input
                      id="noticeTitle"
                      type="text"
                      className="form-input"
                      value={noticeForm.title || ''}
                      onChange={(e) => setNoticeForm({ ...noticeForm, title: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="noticeContent">내용</label>
                    <textarea
                      id="noticeContent"
                      className="form-input"
                      rows={8}
                      value={noticeForm.content || ''}
                      onChange={(e) => setNoticeForm({ ...noticeForm, content: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group-inline">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={noticeForm.isImportant || false}
                        onChange={(e) => setNoticeForm({ ...noticeForm, isImportant: e.target.checked })}
                      />
                      중요 공지 (상단 고정)
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={noticeForm.isPublished !== false}
                        onChange={(e) => setNoticeForm({ ...noticeForm, isPublished: e.target.checked })}
                      />
                      게시
                    </label>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">
                      {editingNoticeId ? '수정' : '작성'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowNoticeForm(false);
                        setEditingNoticeId(null);
                        setNoticeForm({ title: '', content: '', isImportant: false, isPublished: true });
                        setMessage(null);
                      }}
                    >
                      취소
                    </button>
                  </div>
                </form>
              )}

              <div className="notices-list-admin">
                {notices.length === 0 ? (
                  <p className="no-notices">등록된 공지사항이 없습니다.</p>
                ) : (
                  notices.map((notice) => (
                    <div key={notice.id} className={`notice-item-admin ${notice.isImportant ? 'important' : ''} ${!notice.isPublished ? 'unpublished' : ''}`}>
                      <div className="notice-item-header">
                        <div className="notice-item-title-row">
                          <h4>{notice.title}</h4>
                          <div className="notice-badges">
                            {notice.isImportant && <span className="badge badge-important">중요</span>}
                            {!notice.isPublished && <span className="badge badge-draft">미게시</span>}
                          </div>
                        </div>
                        <span className="notice-date">{new Date(notice.createdAt).toLocaleDateString('ko-KR')}</span>
                      </div>
                      <p className="notice-preview">{notice.content.substring(0, 100)}{notice.content.length > 100 ? '...' : ''}</p>
                      <div className="notice-actions">
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleEditNotice(notice)}
                        >
                          수정
                        </button>
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => handleDeleteNotice(notice.id)}
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
